import { AxiosError } from "axios";
import { z } from "zod";

import {
  BUG_REPORT_SEVERITIES,
  CreateBugReportRequestI,
} from "@/api/routes/bug-reports";

/** The backend limits, so the form refuses what the server would. */
export const BUG_REPORT_LIMITS = {
  TITLE: 200,
  DESCRIPTION: 10_000,
  LOOM_URL: 500,
  PAGE_URL: 2_000,
} as const;

const isWebURL = (value: string) => {
  // The backend's Go parser rejects "https:example.com", so ask for the slashes.
  if (!/^https?:\/\//i.test(value)) {
    return false;
  }

  try {
    const url = new URL(value);

    return (
      (url.protocol === "http:" || url.protocol === "https:") && !!url.host
    );
  } catch {
    return false;
  }
};

export const bugReportFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Enter a title.")
    .max(BUG_REPORT_LIMITS.TITLE, "Keep the title to 200 characters.")
    .refine((value) => !/[\r\n]/.test(value), "Keep the title to one line."),
  severity: z.enum(BUG_REPORT_SEVERITIES),
  description: z
    .string()
    .trim()
    .min(1, "Describe the bug.")
    .max(
      BUG_REPORT_LIMITS.DESCRIPTION,
      "Keep the description to 10,000 characters."
    ),
  loom_url: z
    .string()
    .trim()
    .max(BUG_REPORT_LIMITS.LOOM_URL, "That link is too long.")
    .refine(
      (value) => value === "" || isWebURL(value),
      "Enter a full link that starts with https://."
    ),
});

export type BugReportFormValuesT = z.infer<typeof bugReportFormSchema>;

export const BUG_REPORT_FORM_DEFAULTS: BugReportFormValuesT = {
  title: "",
  severity: "medium",
  description: "",
  loom_url: "",
};

/** What the app adds to the caller's report: where they were. */
export interface BugReportContextI {
  pageURL?: string;
  listId?: string;
  campaignId?: string;
}

/**
 * The page link without its query or fragment. A query can hold contact
 * details, such as `phone` and `email`, and the report goes to Linear.
 */
export const pageURLWithoutQuery = (
  value: string | undefined
): string | undefined => {
  if (!value || !isWebURL(value)) {
    return undefined;
  }

  try {
    const url = new URL(value);

    return `${url.origin}${url.pathname}`;
  } catch {
    return undefined;
  }
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A value the backend takes as a list or campaign ID, else undefined. */
export const asUUID = (value: unknown): string | undefined =>
  typeof value === "string" && UUID_PATTERN.test(value) ? value : undefined;

export const buildBugReportRequest = (
  values: BugReportFormValuesT,
  context: BugReportContextI
): CreateBugReportRequestI => {
  const pageURL = pageURLWithoutQuery(context.pageURL)?.slice(
    0,
    BUG_REPORT_LIMITS.PAGE_URL
  );

  return {
    title: values.title.trim(),
    severity: values.severity,
    description: values.description.trim(),
    loom_url: values.loom_url.trim() || undefined,
    page_url: pageURL && isWebURL(pageURL) ? pageURL : undefined,
    list_id: asUUID(context.listId),
    campaign_id: asUUID(context.campaignId),
  };
};

export const BUG_REPORT_FAILURES = {
  TOO_MANY:
    "You sent too many bug reports. You can send 5 an hour. Try again later.",
  NOT_SET_UP: "Bug reports aren't set up yet. Contact support.",
  OTHER: "We couldn't send your bug report. Try again later.",
} as const;

/** A sentence for a failed report: 429, 503 or anything else. */
export const describeBugReportFailure = (error: unknown): string => {
  const status = (error as AxiosError)?.response?.status;

  if (status === 429) {
    return BUG_REPORT_FAILURES.TOO_MANY;
  }

  if (status === 503) {
    return BUG_REPORT_FAILURES.NOT_SET_UP;
  }

  return BUG_REPORT_FAILURES.OTHER;
};
